import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/i18n/LangContext";

/**
 * "Continue with Google" button.
 *
 * Behaviour:
 *   - The button is shown by DEFAULT and stays visible unless the backend
 *     explicitly reports that Google OAuth is not configured
 *     (`GET /users/google/config` returns { enabled: false }).
 *   - This makes the button resilient to slow / cached / temporarily
 *     unreachable API responses on freshly-provisioned VPS boxes: the click
 *     itself will surface a real error if the auth is not configured.
 *
 * Flow: click → GET /users/google/authorize → redirect browser to Google.
 * Google → GET /api/auth/google/callback (backend) → 302 to /{lang}/auth/google?token=...
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS
 */
export default function GoogleAuthButton({ next = "/", disabled }) {
  const { t } = useLang();
  // Default TRUE so the button is visible immediately, even before the config
  // check completes. We only hide it if we get a definite `enabled: false`.
  const [visible, setVisible] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/users/google/config")
      .then((r) => {
        if (!alive) return;
        if (r?.data && r.data.enabled === false) setVisible(false);
      })
      .catch(() => { /* network/config error — keep the button visible */ });
    return () => { alive = false; };
  }, []);

  const click = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.get(`/users/google/authorize?next=${encodeURIComponent(next)}`);
      const url = r?.data?.authorization_url;
      if (!url) throw new Error("no url");
      window.location.href = url;
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data?.detail || t("auth.google_unavailable"));
    }
  };

  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={click}
      disabled={disabled || loading}
      className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#111] font-semibold px-6 py-3 rounded-lg hover:bg-zinc-100 disabled:opacity-60 transition-colors border border-white/10"
      data-testid="google-auth-btn"
    >
      <GoogleGlyph />
      <span>{t("auth.continue_with_google")}</span>
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}
